import { Tag } from '@curri/components'
import { BookOpen, ChartLine, House, SidebarSimple } from '@phosphor-icons/react'
import type { ReactNode } from 'react'

import { CurriWordmark } from './CurriWordmark.tsx'
import {
  DESTINATIONS,
  type Destination,
  type DestinationId,
  type Section,
} from './destinations.ts'
import { isPlainClick } from './links.ts'

// The portal's only nav column: top-level groups, each expanding to its pages.
//
// One column rather than a rail plus a section nav. On the REST reference that
// second column put three sidebars on screen — ours, the pages list, and Scalar's
// own endpoint tree — which reads as depth the portal does not actually have.
//
// Expansion follows the route rather than living in state: the active group is
// open and the others are shut. A group has no page of its own, so clicking one
// opens its first page, which is also what expands it. That keeps "what is open"
// answerable from the URL alone — no state to get out of step with the address
// bar, and a deep link opens with the right group already showing.

const ICONS = { BookOpen, ChartLine, House } as const

export const RAIL_COLLAPSED_KEY = 'curri.portal.railCollapsed'

const ROW_HEIGHT = 36

// `filled` is separate from `active`: when a group is expanded, the page row
// beneath it carries the selection, and filling both stacks two mint blocks into
// one heavy smear. The group keeps the colour and weight, not the background.
const groupRowStyle = (
  active: boolean,
  filled: boolean,
  collapsed: boolean
) => ({
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  height: 38,
  padding: collapsed ? 0 : '0 10px',
  justifyContent: collapsed ? 'center' : 'flex-start',
  border: 0,
  borderRadius: 'var(--curri-radius-md)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: active ? 600 : 500,
  color: active ? 'var(--curri-accent)' : 'var(--curri-text-secondary)',
  background: filled ? 'var(--curri-brand-subtle)' : 'transparent',
})

const sectionRowStyle = (active: boolean, disabled: boolean) =>
  ({
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    height: ROW_HEIGHT,
    // Indented to the group label's text, so the hierarchy reads without a
    // second column drawing it.
    padding: '0 10px 0 38px',
    border: 0,
    borderRadius: 'var(--curri-radius-md)',
    cursor: disabled ? 'default' : 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: 13.5,
    fontWeight: active ? 600 : 500,
    color: disabled
      ? 'var(--curri-text-tertiary)'
      : active
        ? 'var(--curri-accent)'
        : 'var(--curri-text-secondary)',
    background: active ? 'var(--curri-brand-subtle)' : 'transparent',
  }) as const

const SectionRow = ({
  section,
  active,
  onSelect,
}: {
  section: Section
  active: boolean
  onSelect: (section: Section) => void
}) => {
  const contents = (
    <>
      <span style={{ flex: 1 }}>{section.label}</span>
      {section.tag && <Tag color="warning" label={section.tag} />}
      {section.placeholder && (
        <span style={{ fontSize: 10.5, color: 'var(--curri-text-tertiary)' }}>
          Soon
        </span>
      )}
    </>
  )

  // A disabled row is wayfinding, not navigation: a span rather than an anchor,
  // so it carries no href to click, copy, or tab to. An <a aria-disabled> would
  // still offer "open in new tab" — the affordance being withheld.
  if (section.disabled) {
    return (
      <span aria-disabled="true" style={sectionRowStyle(active, true)}>
        {contents}
      </span>
    )
  }

  return (
    <a
      href={section.path}
      aria-current={active ? 'page' : undefined}
      onClick={event => {
        if (!isPlainClick(event)) return
        event.preventDefault()
        onSelect(section)
      }}
      style={sectionRowStyle(active, false)}
    >
      {contents}
    </a>
  )
}

// The collapse control, in the rail header beside the brand — where the product
// chrome puts it. Icon-only: at this size a label would crowd the wordmark.
const CollapseToggle = ({
  collapsed,
  onToggle,
}: {
  collapsed: boolean
  onToggle: () => void
}) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={!collapsed}
    aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
    title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 28,
      height: 28,
      flex: 'none',
      border: 0,
      borderRadius: 'var(--curri-radius-sm)',
      background: 'transparent',
      cursor: 'pointer',
      color: 'var(--curri-text-tertiary)',
    }}
  >
    <SidebarSimple size={17} />
  </button>
)

export const Rail = ({
  activeId,
  activeSectionId,
  collapsed,
  onSelect,
  onSelectSection,
  onToggleCollapsed,
  footer,
}: {
  activeId: DestinationId
  activeSectionId: string
  collapsed: boolean
  onSelect: (destination: Destination) => void
  onSelectSection: (section: Section) => void
  onToggleCollapsed: () => void
  /** Session control, pinned to the bottom like the product chrome's account row. */
  footer: ReactNode
}) => (
  <nav
    aria-label="Portal"
    style={{
      width: collapsed
        ? 'var(--curri-rail-width-collapsed)'
        : 'var(--curri-rail-width)',
      flex: 'none',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      padding: '10px 8px 12px',
      // The base layer the content sheet sits on, so no right border — the
      // sheet's left border is the seam.
      background: 'var(--curri-canvas)',
      overflowY: 'auto',
      transition: 'width 180ms var(--curri-ease)',
    }}
  >
    {/* Brand row. There is no separate header bar: the rail is the chrome, and
        the sheet starts below its own top margin — the product's layout. */}
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        gap: 8,
        minHeight: 38,
        padding: collapsed ? 0 : '2px 6px 0 10px',
        marginBottom: 12,
        color: 'var(--curri-text)',
      }}
    >
      {collapsed ? (
        <CollapseToggle collapsed={collapsed} onToggle={onToggleCollapsed} />
      ) : (
        <>
          {/* Wordmark at the app's brand size (20px), with the product and what
              it is on the line beneath — the shape the app uses for brand over
              workspace. */}
          {/* `alignItems: flex-start` so the wordmark keeps its intrinsic width.
              Stretched to the column, the SVG centres its own content — which is
              why it looked centred rather than flush left. */}
          <span
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 4,
            }}
          >
            <CurriWordmark height={20} />
            <span
              style={{
                // Optically aligns the subtitle's first glyph with the
                // wordmark's, which starts inset from its own bounding box.
                marginLeft: 3,
                fontSize: 11.5,
                fontWeight: 500,
                color: 'var(--curri-text-tertiary)',
              }}
            >
              <span style={{ fontWeight: 600, letterSpacing: '0.04em' }}>
                BUILD
              </span>{' '}
              Developer Platform
            </span>
          </span>
          <CollapseToggle collapsed={collapsed} onToggle={onToggleCollapsed} />
        </>
      )}
    </div>

    {DESTINATIONS.map(destination => {
      const Icon = ICONS[destination.icon]
      const active = destination.id === activeId
      // Collapsed is icon-only, so children would have nothing to hang off.
      const expanded = active && !collapsed && destination.sections.length > 1

      return (
        <div key={destination.id}>
          <a
            href={destination.sections[0].path}
            aria-current={active ? 'page' : undefined}
            aria-expanded={
              destination.sections.length > 1 ? expanded : undefined
            }
            title={collapsed ? destination.label : undefined}
            onClick={event => {
              if (!isPlainClick(event)) return
              event.preventDefault()
              onSelect(destination)
            }}
            style={groupRowStyle(active, active && !expanded, collapsed)}
          >
            <Icon size={18} />
            {!collapsed && (
              <span style={{ flex: 1, textAlign: 'left' }}>
                {destination.label}
              </span>
            )}
          </a>

          {expanded &&
            destination.sections.map(section => (
              <SectionRow
                key={section.id}
                section={section}
                active={section.id === activeSectionId}
                onSelect={onSelectSection}
              />
            ))}
        </div>
      )
    })}

    <div style={{ flex: 1 }} />

    {/* Account rows sit on the nav's rhythm, not a button's — 2px apart, like the
        destinations above, with the divider giving them their own group. The rows
        size themselves in both states, so this stays `stretch`. */}
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        paddingTop: 10,
        marginTop: 4,
        borderTop: '1px solid var(--curri-border)',
      }}
    >
      {footer}
    </div>
  </nav>
)
